import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Bookmark, BookmarkCheck, Check, ChevronLeft, ChevronRight, LayoutGrid, X } from 'lucide-react';
import { studyAPI, cardsAPI } from '../../services/api';
import CommentSection from '../CommentSection/CommentSection';
import RichContent, { isHtml } from '../RichText/RichContent';
import UPSCQuestion from '../UPSCQuestion/UPSCQuestion';
import ImportantTermText from '../ImportantTermText/ImportantTermText';
import './CardBrowser.css';

const toText = value => {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return value.map(toText).filter(Boolean).join('\n');
  if (typeof value === 'object') return toText(value.text ?? value.value ?? value.label ?? '');
  return '';
};

function getCorrectIndex(card) {
  const options = Array.isArray(card?.options) ? card.options : [];
  const marked = options.findIndex(option => option?.isCorrect === true);
  if (marked !== -1) return marked;

  const answer = card?.correctAnswer ?? card?.correctOption ?? card?.answerIndex;
  if (typeof answer === 'number') return answer;
  if (typeof answer !== 'string') return -1;

  const trimmed = answer.trim();
  if (/^\d+$/.test(trimmed)) return Number(trimmed);
  if (/^[A-D]$/i.test(trimmed)) return trimmed.toUpperCase().charCodeAt(0) - 65;
  return options.map(toText).findIndex(option => option.trim() === trimmed);
}

function FormattedText({ text }) {
  if (!text) return null;
  if (isHtml(text)) {
    return <div className="cb-text cb-rich"><RichContent html={text} /></div>;
  }

  const lines = text.split('\n');
  return (
    <div className="cb-text">
      {lines.map((line, index) => {
        const content = line.trim();
        if (!content) return <div key={index} className="cb-gap" />;

        const numbered = content.match(/^(\d+)[.)]\s+(.*)$/);
        const bullet = content.match(/^[•\-*→]\s+(.*)$/);
        if (numbered || bullet) {
          return (
            <div key={index} className="cb-li">
              <b>{numbered ? `${numbered[1]}.` : '•'}</b>
              <span><ImportantTermText text={(numbered ? numbered[2] : bullet[1]).replace(/\*\*/g, '')} /></span>
            </div>
          );
        }

        return <p key={index}><ImportantTermText text={content.replace(/\*\*/g, '')} /></p>;
      })}
    </div>
  );
}

function formatDate(date) {
  const parsed = new Date(`${date}T12:00:00+05:30`);
  if (Number.isNaN(parsed.getTime())) return date;
  return parsed.toLocaleDateString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export default function PrelimsPracticeSession() {
  const { date = '' } = useParams();
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [picked, setPicked] = useState(null);
  const [revealedCorrect, setRevealedCorrect] = useState(null);
  const [checkingAnswer, setCheckingAnswer] = useState(false);
  const [answerError, setAnswerError] = useState('');
  const [navOpen, setNavOpen] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [bookmarkError, setBookmarkError] = useState('');
  const contentRef = useRef(null);
  const answerRequestRef = useRef(0);

  useEffect(() => {
    let active = true;
    const fetchQuestions = async () => {
      setLoading(true);
      setLoadError('');
      setCards([]);
      setSelectedId(null);
      answerRequestRef.current += 1;
      try {
        const response = await studyAPI.getQuestionBank({ date, examType: 'Prelims' });
        if (!active) return;
        const list = (Array.isArray(response.data?.cards) ? response.data.cards : [])
          .filter(item => item?.card?._id);
        setCards(list);
        setSelectedId(list[0]?.card._id || null);
      } catch (error) {
        if (!active) return;
        console.error('Could not load Prelims practice questions:', error);
        setLoadError(error.response?.data?.message || 'Could not load questions for this date. Please try again.');
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchQuestions();
    return () => { active = false; };
  }, [date]);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const previous = [html.style.overflow, body.style.overflow];
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    return () => {
      html.style.overflow = previous[0];
      body.style.overflow = previous[1];
    };
  }, []);

  const { flat, groups } = useMemo(() => {
    const bySubject = new Map();
    cards.forEach(item => {
      const subject = toText(item.card.subject).trim() || 'General';
      if (!bySubject.has(subject)) bySubject.set(subject, []);
      bySubject.get(subject).push(item);
    });

    const items = [];
    const sections = [];
    bySubject.forEach((subjectCards, subject) => {
      sections.push({ subject, ids: subjectCards.map(item => item.card._id) });
      items.push(...subjectCards);
    });
    return { flat: items, groups: sections };
  }, [cards]);

  useEffect(() => {
    if (flat.length && !flat.some(item => item.card._id === selectedId)) {
      setSelectedId(flat[0].card._id);
    } else if (!flat.length && selectedId !== null) {
      setSelectedId(null);
    }
  }, [flat, selectedId]);

  const idx = flat.findIndex(item => item.card._id === selectedId);
  const item = idx >= 0 ? flat[idx] : null;
  const card = item?.card || null;
  const options = (Array.isArray(card?.options) ? card.options : []).map(toText);
  const correct = revealedCorrect ?? (card ? getCorrectIndex(card) : -1);
  const done = picked !== null;

  useEffect(() => {
    answerRequestRef.current += 1;
    setPicked(null);
    setRevealedCorrect(null);
    setCheckingAnswer(false);
    setAnswerError('');
    setBookmarkError('');
    setIsBookmarked(Boolean(item?.progress?.isBookmarked));
    contentRef.current?.scrollTo?.(0, 0);
  }, [selectedId, item?.progress?.isBookmarked]);

  const goTo = id => {
    setSelectedId(id);
    setNavOpen(false);
  };
  const goPrev = useCallback(() => {
    if (idx > 0) setSelectedId(flat[idx - 1].card._id);
  }, [idx, flat]);
  const goNext = useCallback(() => {
    if (idx >= 0 && idx < flat.length - 1) setSelectedId(flat[idx + 1].card._id);
  }, [idx, flat]);

  useEffect(() => {
    const onKeyDown = event => {
      if (event.key === 'Escape') {
        setNavOpen(false);
        return;
      }
      const target = event.target;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'SELECT'
        || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key === 'ArrowRight') goNext();
      if (event.key === 'ArrowLeft') goPrev();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [goNext, goPrev]);

  const pickOption = async optionIndex => {
    if (!card || done || checkingAnswer) return;
    setAnswerError('');
    const requestId = ++answerRequestRef.current;
    const knownAnswer = getCorrectIndex(card);
    if (knownAnswer >= 0) {
      setRevealedCorrect(knownAnswer);
      setPicked(optionIndex);
      return;
    }

    setCheckingAnswer(true);
    try {
      const response = await cardsAPI.getOne(card._id);
      const answerIndex = getCorrectIndex(response.data?.card || {});
      if (answerIndex < 0) throw new Error('The correct answer is unavailable for this question.');
      if (requestId !== answerRequestRef.current) return;
      setRevealedCorrect(answerIndex);
      setPicked(optionIndex);
    } catch (error) {
      if (requestId !== answerRequestRef.current) return;
      console.error('Could not check the selected answer:', error);
      setAnswerError(error.response?.data?.message || error.message || 'Could not check your answer. Please try again.');
    } finally {
      if (requestId === answerRequestRef.current) setCheckingAnswer(false);
    }
  };

  const toggleBookmark = async () => {
    if (!card) return;
    setBookmarkError('');
    try {
      const response = await cardsAPI.bookmark(card._id);
      setIsBookmarked(Boolean(response.data?.isBookmarked));
    } catch (error) {
      console.error('Could not update question bookmark:', error);
      setBookmarkError(error.response?.data?.message || 'Could not update bookmark. Please try again.');
    }
  };

  const screen = (
    <div className="cb">
      <aside className={`cb-side ${navOpen ? 'open' : ''}`} aria-label="Questions">
        <div className="cb-side-body">
          <div className="cb-side-top">
            <h2 style={{ margin: 0, color: 'var(--navy-deep)', fontSize: '1.1rem', fontWeight: 700 }}>Questions</h2>
            <button type="button" className="cb-x" onClick={() => setNavOpen(false)} aria-label="Close questions panel">
              <X size={18} />
            </button>
          </div>
          <nav className="cb-subjects">
            {groups.map(group => (
              <section key={group.subject} className={group.ids.includes(selectedId) ? 'on' : ''}>
                <h2><i className="cb-dot" />{group.subject}</h2>
                <div className="cb-tiles">
                  {group.ids.map((id, number) => (
                    <button
                      key={id}
                      type="button"
                      className={`cb-tile ${id === selectedId ? 'cur' : ''}`}
                      aria-label={`${group.subject}, question ${number + 1}`}
                      aria-current={id === selectedId ? 'true' : undefined}
                      onClick={() => goTo(id)}
                    >
                      {number + 1}
                    </button>
                  ))}
                </div>
              </section>
            ))}
          </nav>
          <button type="button" className="cb-back" onClick={() => navigate('/prelims')}>
            <ArrowLeft size={18} />Back to dates
          </button>
        </div>
      </aside>

      <div className="cb-content" ref={contentRef}>
        <div className="cb-bar">
          <button type="button" className="cb-grid-btn" onClick={() => setNavOpen(true)} aria-label="Show question list">
            <LayoutGrid size={18} />
          </button>
          <h1 style={{ margin: 0, color: 'var(--navy-deep)', fontSize: '1.2rem', fontWeight: 600 }}>
            Practice · {formatDate(date)}
          </h1>
        </div>

        {loading ? (
          <div className="cb-state"><span className="cb-spin" role="status" aria-label="Loading questions" /></div>
        ) : loadError ? (
          <p className="cb-state cb-answer-feedback" role="alert">{loadError}</p>
        ) : !card ? (
          <p className="cb-state cb-muted">No questions found for {formatDate(date)}.</p>
        ) : (
          <>
            <div className="cb-nav">
              <span className="cb-pos">Question {idx + 1} of {flat.length}</span>
            </div>
            <article>
              <section className="cb-card">
                <div className="cb-card-heading">
                  <div className="cb-tags">
                    {toText(card.subject) && <span>{toText(card.subject)}</span>}
                    <span>Prelims</span>
                    {toText(card.difficulty) && <span>{toText(card.difficulty)}</span>}
                    {toText(card.questionType) && <span>{toText(card.questionType)}</span>}
                  </div>
                  <div className="cb-card-actions" aria-label="Question actions">
                    <button
                      type="button"
                      className={`cb-icon-action ${isBookmarked ? 'is-bookmarked' : ''}`}
                      onClick={toggleBookmark}
                      aria-label={isBookmarked ? 'Remove bookmark' : 'Bookmark question'}
                      aria-pressed={isBookmarked}
                      title={isBookmarked ? 'Remove bookmark' : 'Bookmark question'}
                    >
                      {isBookmarked ? <BookmarkCheck size={20} /> : <Bookmark size={20} />}
                    </button>
                  </div>
                </div>

                <div className="cb-qtext"><UPSCQuestion text={toText(card.question)} /></div>

                {options.length > 0 && (
                  <div className="cb-opts" role="group" aria-label="Answer options">
                    {options.map((option, optionIndex) => {
                      const isCorrect = done && optionIndex === correct;
                      const isWrong = done && optionIndex === picked && !isCorrect;
                      const state = isCorrect ? 'ok' : isWrong ? 'bad' : done ? 'dim' : '';
                      return (
                        <button
                          key={optionIndex}
                          type="button"
                          className={`cb-opt ${state} ${done || checkingAnswer ? 'locked' : ''}`}
                          disabled={done || checkingAnswer}
                          aria-pressed={optionIndex === picked}
                          onClick={() => pickOption(optionIndex)}
                        >
                          <b>{String.fromCharCode(65 + optionIndex)}.</b>
                          <span>{option}</span>
                          {isCorrect && <Check size={17} aria-label="Correct" />}
                          {isWrong && <X size={17} aria-label="Incorrect" />}
                        </button>
                      );
                    })}
                  </div>
                )}

                {(checkingAnswer || answerError) && (
                  <p className="cb-answer-feedback" role={answerError ? 'alert' : 'status'}>
                    {checkingAnswer ? 'Checking answer…' : answerError}
                  </p>
                )}

                {done && options.length > 0 && (
                  <p className="cb-answer-feedback" role="status">
                    {correct === picked ? 'Correct!' : 'Incorrect'}
                  </p>
                )}
                {done && card.answer && (
                  <div className="cb-a"><FormattedText text={toText(card.answer)} /></div>
                )}
                {done && card.explanation && (
                  <div className="cb-a">
                    <strong>Explanation</strong>
                    <FormattedText text={toText(card.explanation)} />
                  </div>
                )}
                {bookmarkError && <p className="cb-answer-feedback" role="alert">{bookmarkError}</p>}
              </section>

              {done && (
                <div className="cb-comments">
                  <CommentSection cardId={card._id} isLoggedIn />
                </div>
              )}

              {options.length === 0 && !done && (
                <div className="cb-nav">
                  <button
                    type="button"
                    className="cb-nbtn"
                    onClick={() => setPicked(0)}
                    style={{ width: 'auto', padding: '0 16px', borderRadius: '999px' }}
                  >
                    Show answer
                  </button>
                </div>
              )}

              {flat.length > 1 && (
                <div className="cb-nav">
                  <button type="button" className="cb-nbtn" onClick={goPrev} disabled={idx <= 0} aria-label="Previous question">
                    <ChevronLeft size={18} />
                  </button>
                  <span className="cb-pos">{idx + 1}/{flat.length}</span>
                  <button type="button" className="cb-nbtn" onClick={goNext} disabled={idx >= flat.length - 1} aria-label="Next question">
                    <ChevronRight size={18} />
                  </button>
                </div>
              )}
            </article>
          </>
        )}
      </div>

      <button
        type="button"
        className={`cb-backdrop ${navOpen ? 'show' : ''}`}
        onClick={() => setNavOpen(false)}
        aria-label="Close question list"
      />
    </div>
  );

  return createPortal(screen, document.body);
}
